<template>
  <div class="reports-hub">
    <!-- Header -->
    <div class="hub-header">
      <div class="hub-header-inner">
        <h1 class="hub-title">
          <i class="mdi mdi-chart-box-outline"></i>
          Analytics Hub
        </h1>
        <p class="hub-subtitle">Access all sales reports, performance insights, and analytics</p>
        <el-input
          v-model="search"
          prefix-icon="el-icon-search"
          placeholder="Search reports..."
          clearable
          size="medium"
          class="hub-search"
        />
      </div>
    </div>

    <div class="hub-content">
      <!-- Quick Stats Bar -->
      <el-row :gutter="16" class="quick-stats-bar">
        <el-col :xs="12" :sm="6" v-for="stat in quickStats" :key="stat.label">
          <div class="quick-stat">
            <i :class="stat.icon" :style="{ color: stat.color }"></i>
            <div>
              <div class="qs-value">{{ stat.value }}</div>
              <div class="qs-label">{{ stat.label }}</div>
            </div>
          </div>
        </el-col>
      </el-row>

      <!-- Report Categories -->
      <div v-for="group in filteredGroups" :key="group.title" class="report-group">
        <h3 class="group-title">
          <i :class="group.icon"></i>
          {{ group.title }}
        </h3>
        <el-row :gutter="16">
          <el-col
            :xs="24"
            :sm="12"
            :md="8"
            :lg="6"
            v-for="report in group.reports"
            :key="report.name"
            class="report-col"
          >
            <div class="report-card" @click="navigate(report.route)">
              <div class="rc-icon" :style="{ background: report.color }">
                <i :class="report.icon"></i>
              </div>
              <div class="rc-body">
                <div class="rc-name">{{ report.name }}</div>
                <div class="rc-desc">{{ report.description }}</div>
              </div>
              <div class="rc-arrow">
                <i class="el-icon-arrow-right"></i>
              </div>
            </div>
          </el-col>
        </el-row>
      </div>

      <div v-if="filteredGroups.length === 0" class="no-results">
        <i class="mdi mdi-magnify"></i>
        <p>No reports match "<strong>{{ search }}</strong>"</p>
        <el-button type="text" @click="search = ''">Clear search</el-button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ReportsHub',

  data () {
    return {
      search: '',

      quickStats: [
        { label: 'Sales Reports', value: '8', icon: 'mdi mdi-chart-line', color: '#409EFF' },
        { label: 'Item Reports', value: '6', icon: 'mdi mdi-package-variant', color: '#67C23A' },
        { label: 'Employee Reports', value: '6', icon: 'mdi mdi-account-group', color: '#E6A23C' },
        { label: 'Analytics', value: '5', icon: 'mdi mdi-lightning-bolt', color: '#9C27B0' }
      ],

      reportGroups: [
        {
          title: 'Sales Overview',
          icon: 'mdi mdi-chart-line',
          reports: [
            { name: 'Sales Summary', description: 'KPIs, trends and top-line metrics for the period', icon: 'mdi mdi-speedometer', color: '#409EFF', route: '/reporting/sales-summary' },
            { name: 'Query Sales', description: 'Search and filter all individual sale records', icon: 'mdi mdi-receipt-text-outline', color: '#5C6BC0', route: '/reporting/query-sales' },
            { name: 'Sales by Period', description: 'Revenue breakdown by day, week or month', icon: 'mdi mdi-calendar-range', color: '#26A69A', route: '/reporting/sales-summary' },
            { name: 'Sales Trends', description: 'Long-term revenue trend lines and growth rates', icon: 'mdi mdi-trending-up', color: '#66BB6A', route: '/reporting/sales-summary' }
          ]
        },
        {
          title: 'Customer Analysis',
          icon: 'mdi mdi-account-group',
          reports: [
            { name: 'Sales by Customer', description: 'Revenue, orders and growth per customer', icon: 'mdi mdi-account-cash', color: '#EF5350', route: '/reporting/sales-by-customer' },
            { name: 'Customer Lifetime Value', description: 'CLV, purchase frequency and basket size', icon: 'mdi mdi-account-star', color: '#AB47BC', route: '/reporting/sales-by-customer' }
          ]
        },
        {
          title: 'Product & Category',
          icon: 'mdi mdi-package-variant',
          reports: [
            { name: 'Sales by Item', description: 'Item-level revenue, units sold and margin', icon: 'mdi mdi-cube-outline', color: '#FF7043', route: '/reporting/sales-by-item' },
            { name: 'Sales by Category', description: 'Category performance and market share', icon: 'mdi mdi-shape-outline', color: '#26C6DA', route: '/reporting/sales-by-category' },
            { name: 'Sales by Modifier', description: 'Add-on and modifier revenue analysis', icon: 'mdi mdi-playlist-plus', color: '#8D6E63', route: '/reporting/sales-by-modifier' },
            { name: 'Sales by Variant', description: 'Product variant performance comparison', icon: 'mdi mdi-code-tags', color: '#78909C', route: '/reporting/sales-by-variant' }
          ]
        },
        {
          title: 'Employee Performance',
          icon: 'mdi mdi-account-chart',
          reports: [
            { name: 'Sales by Employee', description: 'Employee revenue, orders and commission', icon: 'mdi mdi-account-tie', color: '#42A5F5', route: '/reporting/sales-by-employee' }
          ]
        },
        {
          title: 'Payments & Finance',
          icon: 'mdi mdi-credit-card-outline',
          reports: [
            { name: 'Sales by Payment Method', description: 'Revenue split by cash, card and mobile', icon: 'mdi mdi-cash-multiple', color: '#EC407A', route: '/reporting/sales-by-payment-method' }
          ]
        }
      ]
    }
  },

  computed: {
    filteredGroups () {
      if (!this.search.trim()) return this.reportGroups
      const q = this.search.toLowerCase()
      return this.reportGroups
        .map(group => ({
          ...group,
          reports: group.reports.filter(r =>
            r.name.toLowerCase().includes(q) || r.description.toLowerCase().includes(q)
          )
        }))
        .filter(group => group.reports.length > 0)
    }
  },

  methods: {
    navigate (route) {
      if (this.$router) {
        this.$router.push(route).catch(() => {})
      }
    }
  }
}
</script>

<style scoped>
.reports-hub {
  background: #f5f7fa;
  min-height: 100vh;
}

.hub-header {
  background: linear-gradient(135deg, #1565C0 0%, #283593 100%);
  padding: 40px 20px;
  text-align: center;
}

.hub-header-inner {
  max-width: 600px;
  margin: 0 auto;
}

.hub-title {
  color: white;
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.hub-subtitle {
  color: rgba(255,255,255,0.8);
  font-size: 14px;
  margin: 0 0 20px 0;
}

.hub-search {
  max-width: 400px;
}

.hub-search >>> .el-input__inner {
  border-radius: 20px;
  border: none;
  box-shadow: 0 2px 8px rgba(0,0,0,0.2);
}

.hub-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 24px 20px;
}

.quick-stats-bar {
  background: white;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
}

.quick-stat {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px;
}

.quick-stat i {
  font-size: 28px;
}

.qs-value {
  font-size: 20px;
  font-weight: 700;
  color: #303133;
  line-height: 1;
}

.qs-label {
  font-size: 12px;
  color: #909399;
  margin-top: 2px;
}

.report-group {
  margin-bottom: 28px;
}

.group-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin: 0 0 12px 0;
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 8px;
  border-bottom: 2px solid #EBEEF5;
}

.report-col {
  margin-bottom: 12px;
}

.report-card {
  display: flex;
  align-items: center;
  background: white;
  border-radius: 8px;
  padding: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
  gap: 12px;
  border: 1px solid #EBEEF5;
}

.report-card:hover {
  box-shadow: 0 4px 16px rgba(0,0,0,0.12);
  transform: translateY(-2px);
  border-color: #409EFF;
}

.rc-icon {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.rc-icon i {
  font-size: 22px;
  color: white;
}

.rc-body {
  flex: 1;
  min-width: 0;
}

.rc-name {
  font-weight: 600;
  font-size: 13px;
  color: #303133;
  margin-bottom: 3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rc-desc {
  font-size: 11px;
  color: #909399;
  line-height: 1.4;
}

.rc-arrow {
  color: #C0C4CC;
  font-size: 14px;
  flex-shrink: 0;
}

.report-card:hover .rc-arrow {
  color: #409EFF;
}

.no-results {
  text-align: center;
  padding: 60px 20px;
  color: #C0C4CC;
}

.no-results i {
  font-size: 64px;
  margin-bottom: 16px;
  display: block;
}

.no-results p {
  font-size: 16px;
  color: #909399;
  margin-bottom: 12px;
}
</style>
